import React, { createContext, useState, useCallback, useEffect } from "react";
import { fetchExecutives, addUser as addUserApi, getUserById as getUserByIdApi, editUser as editUserApi } from "../../Services/userapis";
import { getValuesFromLocalstorage } from "../../components/Helpers";
import LOCAL_KEYS, { setToLocalStorage, parseLocalStorageJSON } from "../../helpers/localstorage";

export const UserContext = createContext();

export const UserProvider = ({ children }) => {
  const [usersList, setUsersList] = useState([]);
  const [userLoading, setUserLoading] = useState(false);
  const [userError, setUserError] = useState(null);
  const [totalUsers, setTotalUsers] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [currentUserDetails, setCurrentUserDetails] = useState(() => {
    // Initialize from local storage on first render
    const details = getValuesFromLocalstorage(LOCAL_KEYS.USER_DETAILS);
    return details;
  });
  const [isPasswordExpired, setIsPasswordExpired] = useState(() => {
    // Initialize password expiry state from localStorage
    return getValuesFromLocalstorage(LOCAL_KEYS.PASSWORD_EXPIRED) || false;
  });

  const [addUserLoading, setAddUserLoading] = useState(false);
  const [addUserError, setAddUserError] = useState(null);

  const fetchUsersList = useCallback(async (page = 0, size = 10, filters = {}) => {
    // Check if password is expired before making API call
    const isPasswordExpired = parseLocalStorageJSON(LOCAL_KEYS.PASSWORD_EXPIRED);
    if (isPasswordExpired) {
      setUserLoading(false);
      return { content: [], totalElements: 0, totalPages: 0 };
    }

    setUserLoading(true);
    setUserError(null);
    try {
      const data = await fetchExecutives(page, size, filters);
      setUsersList(data.content || []);
      setTotalUsers(data.totalElements || 0);
      setTotalPages(data.totalPages || 0);
      return data;
    } catch (error) {
      // Try to extract backend error message
      let backendMsg = error?.response?.data?.message || error?.message || "Failed to fetch users";
      setUserError(backendMsg);
      setUsersList([]);
      setTotalUsers(0);
      setTotalPages(0);
      // Throw error so UI can show toast with correct message
      throw new Error(backendMsg);
    } finally {
      setUserLoading(false);
    }
  }, []);

  const addUser = useCallback(async (userData) => {
    setAddUserLoading(true);
    setAddUserError(null);
    try {
      const result = await addUserApi(userData);
      return result;
    } catch (error) {
      setAddUserError(error.message || "Failed to add user");
      throw error;
    } finally {
      setAddUserLoading(false);
    }
  }, []);

  // Add getUserById for Edit Employee integration
  const getUserById = useCallback(async (id) => {
    setUserLoading(true);
    setUserError(null);
    try {
      const user = await getUserByIdApi(id);
      return user;
    } catch (error) {
      setUserError(error.message || "Failed to fetch user");
      throw error;
    } finally {
      setUserLoading(false);
    }
  }, []);

  const editUser = useCallback(async (id, userData) => {
    setUserLoading(true);
    setUserError(null);
    try {
      const result = await editUserApi(id, userData);
      // Do NOT update local storage or currentUserDetails here as this is for editing other users.
      // These should only be updated for the currently logged-in user's own profile updates.
      return result;
    } catch (error) {
      setUserError(error.message || "Failed to edit user");
      throw error;
    } finally {
      setUserLoading(false);
    }
  }, []);

  // Function to update password expiry state
  const updatePasswordExpiredState = useCallback((expired) => {
    setIsPasswordExpired(expired);
    if (expired) {
      setToLocalStorage(LOCAL_KEYS.PASSWORD_EXPIRED, true);
    } else {
      localStorage.removeItem(LOCAL_KEYS.PASSWORD_EXPIRED);
    }
  }, []);

  // Update currentUserDetails when local storage changes (e.g., on login/logout)
  useEffect(() => {
    const handleStorageChange = () => {
      const updatedDetails = getValuesFromLocalstorage(LOCAL_KEYS.USER_DETAILS);
      const passwordExpired = getValuesFromLocalstorage(LOCAL_KEYS.PASSWORD_EXPIRED) || false;
      setCurrentUserDetails(updatedDetails);
      setIsPasswordExpired(passwordExpired);
    };

    window.addEventListener('storage', handleStorageChange);
    return () => {
      window.removeEventListener('storage', handleStorageChange);
    };
  }, []);

  // Fetch users on initial load
  useEffect(() => {
    // Check if password is expired before making API call
    const isPasswordExpired = parseLocalStorageJSON(LOCAL_KEYS.PASSWORD_EXPIRED);
    if (isPasswordExpired) {
      return;
    }

    const token = localStorage.getItem('access_token');
    const userDetails = getValuesFromLocalstorage(LOCAL_KEYS.USER_DETAILS);
    if (token && userDetails && userDetails.acctType === "ADMIN") {
      fetchUsersList(0, 10, { stsCd: "A" }); // Only fetch active users by default for ADMIN
    }
  }, [fetchUsersList]);

  return (
    <UserContext.Provider
      value={{
        usersList,
        fetchUsersList,
        userLoading,
        userError,
        addUser,
        addUserLoading,
        addUserError,
        getUserById,
        editUser,
        totalUsers,
        totalPages,
        currentUserDetails, // Provide currentUserDetails in context
        setCurrentUserDetails, // Provide setter for currentUserDetails
        isPasswordExpired, // Provide password expiry state
        updatePasswordExpiredState, // Provide function to update password expiry state
      }}
    >
      {children}
    </UserContext.Provider>
  );
};